/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
// eslint-disable-next-line import/no-unresolved
import { MenuItem } from "primereact/menuitem";

import { useSmartNavigation } from "../../../helpers/navigation";
import { readBreadcrumbState, writeBreadcrumbState } from "../../../helpers/breadcrumbStorage";
import {
  BREADCRUMB_RESET_POINTS,
  type AppRoot,
  type BreadcrumbDisplay,
  type BreadcrumbEntry,
  type BreadcrumbRoot,
  type BreadcrumbState,
  type BreadcrumbContextType,
} from "./types";

const DEFAULT_ROOT: BreadcrumbRoot = { label: "My Journey", route: "/" };

/** Get the parent path by stripping the last segment. "/a/b/c" → "/a/b" */
function getParentRoute(route: string): string {
  const lastSlash = route.lastIndexOf("/");
  if (lastSlash <= 0) return "/";
  return route.substring(0, lastSlash);
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

interface BreadcrumbProviderProps {
  /** @deprecated Root is now set dynamically by reset points. Used only as initial default. */
  appRoot?: AppRoot;
  children: ReactNode;
}

export const BreadcrumbProvider: React.FC<BreadcrumbProviderProps> = ({ appRoot, children }) => {
  const { navigateTo } = useSmartNavigation();

  // Convert deprecated appRoot prop to a BreadcrumbRoot for initialization only
  const initialRoot: BreadcrumbRoot =
    appRoot === "admin" ? { label: "Admin", route: "/admin" } : DEFAULT_ROOT;

  // Initialize from sessionStorage, falling back to initialRoot.
  // Trail is always restored — reset points and resetTrail flags on
  // individual pages handle clearing stale trails from other apps.
  const [state, setState] = useState<BreadcrumbState>(() => {
    const stored = readBreadcrumbState();
    return {
      root: stored?.root ?? initialRoot,
      trail: stored?.trail ?? [],
      display: stored?.display ?? "navbar",
    };
  });

  // Track whether breadcrumbs should be visible in the current render.
  // Separate from persisted trail so unmounting hides breadcrumbs without wiping storage.
  const [displayTrail, setDisplayTrail] = useState<BreadcrumbEntry[]>(state.trail);
  const [displayMode, setDisplayMode] = useState<BreadcrumbDisplay>(state.display);

  // Persist to sessionStorage whenever state changes
  useEffect(() => {
    writeBreadcrumbState(state);
    setDisplayTrail(prev => {
      if (prev.length === 0 && state.trail.length === 0) return prev;
      return state.trail;
    });
    setDisplayMode(state.display);
  }, [state]);

  // Compute the home MenuItem from the dynamic root
  const homeItem: MenuItem = useMemo(
    () => ({
      label: state.root.label,
      command: () => navigateTo(state.root.route),
    }),
    [state.root, navigateTo]
  );

  // Convert displayTrail to PrimeReact MenuItem[] for rendering
  const breadcrumbs: MenuItem[] | undefined = useMemo(() => {
    if (displayTrail.length === 0) return undefined;
    return displayTrail.map((entry, index) => {
      const isLast = index === displayTrail.length - 1;
      return {
        label: entry.label,
        ...(isLast
          ? { "aria-current": "page" as const }
          : { command: () => navigateTo(entry.route) }),
      };
    });
  }, [displayTrail, navigateTo]);

  /**
   * Core trail algorithm: truncate + append with auto reset-point detection.
   *
   * 1. If entry.route matches a BREADCRUMB_RESET_POINTS key → clear trail, set root
   * 2. If resetTrail is true → clear trail, keep current root, start fresh with this entry
   * 3. If entry.route already exists in trail → truncate to that point (back-navigation)
   * 4. If a sibling route exists in trail (same parent path) → replace from that point
   * 5. Otherwise → append to end (forward-navigation)
   */
  const updateTrail = useCallback(
    (entry: BreadcrumbEntry, display?: BreadcrumbDisplay, resetTrail?: boolean) => {
      setState(prev => {
        const resetRoot = BREADCRUMB_RESET_POINTS[entry.route];

        if (resetRoot) {
          // Reset point: clear trail, set new root. The page IS the root, so don't add to trail.
          const resolvedDisplay = display ?? "navbar";
          if (
            prev.root === resetRoot &&
            prev.trail.length === 0 &&
            prev.display === resolvedDisplay
          ) {
            return prev; // Already at reset point — no state change needed
          }
          return { root: resetRoot, trail: [], display: resolvedDisplay };
        }

        // Explicit trail reset: clear trail and start with just this entry
        if (resetTrail) {
          return {
            ...prev,
            trail: [entry],
            display: display ?? prev.display,
          };
        }

        // Non-reset: check if route already exists in trail
        const existingIndex = prev.trail.findIndex(e => e.route === entry.route);

        let newTrail: BreadcrumbEntry[];
        if (existingIndex !== -1) {
          // Route exists: truncate everything after it, update label
          newTrail = prev.trail.slice(0, existingIndex + 1);
          newTrail[existingIndex] = entry;
        } else {
          // Check for sibling routes (same parent path) already in the trail.
          // This handles the case where a parent layout stays mounted and the user
          // navigates between sibling child routes — the old sibling (and anything
          // deeper) should be replaced, not appended after.
          const newParent = getParentRoute(entry.route);
          const siblingIndex = prev.trail.findIndex(e => getParentRoute(e.route) === newParent);

          if (siblingIndex !== -1) {
            // Sibling found: truncate to before it, then append new entry
            newTrail = [...prev.trail.slice(0, siblingIndex), entry];
          } else {
            // New route, no sibling: append
            newTrail = [...prev.trail, entry];
          }
        }

        return {
          ...prev,
          trail: newTrail,
          display: display ?? prev.display,
        };
      });
    },
    []
  );

  // Legacy setBreadcrumbs for backward compat with useNavContext consumers
  const setBreadcrumbs = useCallback((items: MenuItem[] | undefined) => {
    if (!items) {
      setDisplayTrail([]);
      return;
    }
    const trail: BreadcrumbEntry[] = items.map(item => ({
      label: item.label || "",
      route: "",
    }));
    setState(prev => ({ ...prev, trail }));
  }, []);

  /**
   * Hide breadcrumbs from display without clearing sessionStorage.
   * Used during component unmount to restore the normal navbar,
   * while preserving trail data for cross-app navigation.
   */
  const hideDisplay = useCallback(() => {
    setDisplayTrail(prev => (prev.length === 0 ? prev : []));
    setDisplayMode("navbar");
  }, []);

  const value: BreadcrumbContextType = useMemo(
    () => ({
      breadcrumbs,
      homeItem,
      display: displayMode,
      updateTrail,
      setBreadcrumbs,
      _hideDisplay: hideDisplay,
    }),
    [breadcrumbs, homeItem, displayMode, updateTrail, setBreadcrumbs, hideDisplay]
  );

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
};

/**
 * Access the breadcrumb context. Must be used within a BreadcrumbProvider.
 */
export const useBreadcrumbContext = (): BreadcrumbContextType => {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error("useBreadcrumbContext must be used within a BreadcrumbProvider");
  }
  return context;
};

/**
 * Try to access the breadcrumb context. Returns undefined if not within a BreadcrumbProvider.
 * Useful for components that need to work with or without the provider (e.g. UserNavBar).
 */
export const useBreadcrumbContextOptional = (): BreadcrumbContextType | undefined => {
  return useContext(BreadcrumbContext);
};
